import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, updateDoc, collection, getDocs, deleteDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useToast } from '../contexts/ToastContext';
import { useConfirm } from '../contexts/ConfirmContext';
import '../styles/SubmissionTemplateEditor.css';

const SubmissionTemplateEditor = () => {
  const toast = useToast();
  const confirm = useConfirm();
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('default');
  const [template, setTemplate] = useState({
    title: 'Annual Submission Portal',
    description: 'Submit your annual requirements including sermon links, disciplinary questions, and personal statements.',
    sections: {
      sermonLinks: {
        enabled: true,
        title: 'Sermon Links',
        description: 'Please provide links to your sermons. Include the title, URL, and a brief description of each sermon.',
        required: true,
        minSermons: 1,
        maxSermons: 10,
        fields: {
          title: { enabled: true, required: true, label: 'Sermon Title' },
          url: { enabled: true, required: true, label: 'URL/Link' },
          description: { enabled: true, required: false, label: 'Description' }
        }
      },
      disciplinaryQuestions: {
        enabled: true,
        title: 'Disciplinary Questions',
        description: 'Please answer the following questions from The Book of Discipline.',
        required: true,
        questions: [
          { id: 1, question: 'Have you studied the doctrines of The United Methodist Church?', enabled: true, required: true },
          { id: 2, question: 'After full examination do you believe that our doctrines are in harmony with the Holy Scriptures?', enabled: true, required: true },
          { id: 3, question: 'Will you preach and maintain them?', enabled: true, required: true },
          { id: 4, question: 'Have you studied our form of Church discipline and polity?', enabled: true, required: true },
          { id: 5, question: 'Do you approve our Church government and polity?', enabled: true, required: true },
          { id: 6, question: 'Will you support and maintain them?', enabled: true, required: true },
          { id: 7, question: 'Will you diligently instruct the children in every place?', enabled: true, required: true },
          { id: 8, question: 'Will you visit from house to house?', enabled: true, required: true },
          { id: 9, question: 'Will you recommend fasting or abstinence, both by precept and example?', enabled: true, required: true }
        ]
      },
      personalStatement: {
        enabled: true,
        title: 'Personal Statement',
        description: 'Please provide a personal statement reflecting on your spiritual journey and growth this year.',
        required: true,
        placeholder: 'Share your spiritual journey, growth experiences, challenges, and insights from this year...',
        minLength: 100,
        maxLength: 5000
      },
      callStatement: {
        enabled: true,
        title: 'Call Statement',
        description: 'Please describe your understanding of your call to ministry and how it has evolved.',
        required: true,
        placeholder: 'Describe your call to ministry, how it has developed, and your sense of direction...',
        minLength: 100,
        maxLength: 5000
      },
      additionalSections: []
    },
    settings: {
      allowDrafts: true,
      showProgress: true,
      emailNotifications: true,
      deadlineReminders: true
    }
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('general');

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    if (selectedTemplateId) {
      fetchTemplate(selectedTemplateId);
    }
  }, [selectedTemplateId]);

  const fetchTemplates = async () => {
    try {
      const templatesRef = collection(db, 'submissionTemplates');
      const templatesSnapshot = await getDocs(templatesRef);
      const templatesList = templatesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Add default templates if none exist
      if (templatesList.length === 0) {
        await createDefaultTemplates();
        return fetchTemplates();
      }
      
      setTemplates(templatesList);
      
      // Set default template if none selected
      if (!selectedTemplateId && templatesList.length > 0) {
        setSelectedTemplateId(templatesList[0].id);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const createDefaultTemplates = async () => {
    const defaultTemplates = [
      {
        id: 'local-pastor',
        name: 'Local Pastor',
        conferenceStatus: 'Local Pastor',
        ...template
      },
      {
        id: 'ministry-candidate',
        name: 'Ministry Candidate',
        conferenceStatus: 'Ministry Candidate',
        ...template
      },
      {
        id: 'clm',
        name: 'CLM',
        conferenceStatus: 'CLM',
        ...template
      },
      {
        id: 'provisional-elder',
        name: 'Provisional Elder',
        conferenceStatus: 'Provisional Elder',
        ...template
      },
      {
        id: 'provisional-deacon',
        name: 'Provisional Deacon',
        conferenceStatus: 'Provisional Deacon',
        ...template
      }
    ];

    for (const template of defaultTemplates) {
      await setDoc(doc(db, 'submissionTemplates', template.id), template);
    }
  };

  const fetchTemplate = async (templateId) => {
    try {
      const templateDoc = await getDoc(doc(db, 'submissionTemplates', templateId));
      if (templateDoc.exists()) {
        setTemplate({ ...template, ...templateDoc.data() });
      }
    } catch (error) {
      console.error('Error fetching template:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, 'submissionTemplates', selectedTemplateId), {
        ...template,
        updatedAt: new Date()
      });
      toast.success('Template saved successfully!');
      fetchTemplates();
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Error saving template. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const createNewTemplate = async () => {
    const templateName = prompt('Enter template name:');
    if (!templateName) return;

    const conferenceStatus = prompt('Enter conference status for this template (e.g., Local Pastor, Ministry Candidate, CLM):');
    if (!conferenceStatus) return;

    const templateId = templateName.toLowerCase().replace(/\s+/g, '-');
    
    try {
      // Create a fresh template with default values
      const newTemplate = {
        id: templateId,
        name: templateName,
        conferenceStatus: conferenceStatus,
        title: 'Annual Submission Portal',
        description: 'Submit your annual requirements including sermon links, disciplinary questions, and personal statements.',
        sections: {
          sermonLinks: {
            enabled: true,
            title: 'Sermon Links',
            description: 'Please provide links to your sermons. Include the title, URL, and a brief description of each sermon.',
            required: true,
            minSermons: 1,
            maxSermons: 10,
            fields: {
              title: { enabled: true, required: true, label: 'Sermon Title' },
              url: { enabled: true, required: true, label: 'URL/Link' },
              description: { enabled: true, required: false, label: 'Description' }
            }
          },
          disciplinaryQuestions: {
            enabled: true,
            title: 'Disciplinary Questions',
            description: 'Please answer the following questions from The Book of Discipline.',
            required: true,
            questions: [
              { id: 1, question: 'Have you studied the doctrines of The United Methodist Church?', enabled: true, required: true },
              { id: 2, question: 'After full examination do you believe that our doctrines are in harmony with the Holy Scriptures?', enabled: true, required: true },
              { id: 3, question: 'Will you preach and maintain them?', enabled: true, required: true },
              { id: 4, question: 'Have you studied our form of Church discipline and polity?', enabled: true, required: true },
              { id: 5, question: 'Do you approve our Church government and polity?', enabled: true, required: true },
              { id: 6, question: 'Will you support and maintain them?', enabled: true, required: true },
              { id: 7, question: 'Will you diligently instruct the children in every place?', enabled: true, required: true },
              { id: 8, question: 'Will you visit from house to house?', enabled: true, required: true },
              { id: 9, question: 'Will you recommend fasting or abstinence, both by precept and example?', enabled: true, required: true }
            ]
          },
          personalStatement: {
            enabled: true,
            title: 'Personal Statement',
            description: 'Please provide a personal statement reflecting on your spiritual journey and growth this year.',
            required: true,
            placeholder: 'Share your spiritual journey, growth experiences, challenges, and insights from this year...',
            minLength: 100,
            maxLength: 5000
          },
          callStatement: {
            enabled: true,
            title: 'Call Statement',
            description: 'Please describe your understanding of your call to ministry and how it has evolved.',
            required: true,
            placeholder: 'Describe your call to ministry, how it has developed, and your sense of direction...',
            minLength: 100,
            maxLength: 5000
          },
          additionalSections: []
        },
        settings: {
          allowDrafts: true,
          showProgress: true,
          emailNotifications: true,
          deadlineReminders: true
        },
        createdAt: new Date()
      };

      await setDoc(doc(db, 'submissionTemplates', templateId), newTemplate);
      await fetchTemplates();
      setSelectedTemplateId(templateId);
      toast.success('New template created.');
    } catch (error) {
      console.error('Error creating template:', error);
      toast.error('Error creating template. Please try again.');
    }
  };

  const duplicateTemplate = async () => {
    const currentTemplate = templates.find(t => t.id === selectedTemplateId);
    if (!currentTemplate) return;

    const templateName = prompt('Enter name for duplicated template:', `${currentTemplate.name} Copy`);
    if (!templateName) return;

    const templateId = templateName.toLowerCase().replace(/\s+/g, '-') + '-' + Date.now();

    try {
      const duplicatedTemplate = { ...currentTemplate, id: templateId, name: templateName, createdAt: new Date() };
      await setDoc(doc(db, 'submissionTemplates', templateId), duplicatedTemplate);
      await fetchTemplates();
      setSelectedTemplateId(templateId);
      toast.success('Template duplicated.');
    } catch (error) {
      console.error('Error duplicating template:', error);
      toast.error('Error duplicating template. Please try again.');
    }
  };

  const deleteTemplate = async () => {
    if (templates.length <= 1) {
      toast.warning('Cannot delete the last template.');
      return;
    }

    const currentTemplate = templates.find(t => t.id === selectedTemplateId);
    if (!currentTemplate) return;

    const confirmed = await confirm({
      title: 'Delete Template',
      message: `Are you sure you want to delete "${currentTemplate.name}"? This action cannot be undone.`,
      confirmLabel: 'Delete',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await deleteDoc(doc(db, 'submissionTemplates', selectedTemplateId));
      await fetchTemplates();
      const remainingTemplates = templates.filter(t => t.id !== selectedTemplateId);
      if (remainingTemplates.length > 0) setSelectedTemplateId(remainingTemplates[0].id);
      toast.success('Template deleted.');
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Error deleting template. Please try again.');
    }
  };

  const updateSection = (sectionKey, updates) => {
    setTemplate(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [sectionKey]: {
          ...prev.sections[sectionKey],
          ...updates
        }
      }
    }));
  };

  const updateQuestion = (questionId, updates) => {
    setTemplate(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        disciplinaryQuestions: {
          ...prev.sections.disciplinaryQuestions,
          questions: prev.sections.disciplinaryQuestions.questions.map(q =>
            q.id === questionId ? { ...q, ...updates } : q
          )
        }
      }
    }));
  };

  const addQuestion = () => {
    const newId = Math.max(...template.sections.disciplinaryQuestions.questions.map(q => q.id)) + 1;
    setTemplate(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        disciplinaryQuestions: {
          ...prev.sections.disciplinaryQuestions,
          questions: [
            ...prev.sections.disciplinaryQuestions.questions,
            { id: newId, question: '', enabled: true, required: true }
          ]
        }
      }
    }));
  };

  const removeQuestion = (questionId) => {
    setTemplate(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        disciplinaryQuestions: {
          ...prev.sections.disciplinaryQuestions,
          questions: prev.sections.disciplinaryQuestions.questions.filter(q => q.id !== questionId)
        }
      }
    }));
  };

  const addCustomSection = () => {
    const newSection = {
      id: Date.now(),
      title: 'New Section',
      description: '',
      enabled: true,
      required: false,
      type: 'text',
      placeholder: '',
      options: []
    };
    
    setTemplate(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        additionalSections: [...prev.sections.additionalSections, newSection]
      }
    }));
  };

  const updateCustomSection = (sectionId, updates) => {
    setTemplate(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        additionalSections: prev.sections.additionalSections.map(section =>
          section.id === sectionId ? { ...section, ...updates } : section
        )
      }
    }));
  };

  const removeCustomSection = (sectionId) => {
    setTemplate(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        additionalSections: prev.sections.additionalSections.filter(section => section.id !== sectionId)
      }
    }));
  };

  if (loading) {
    return (
      <div className="template-editor">
        <div className="loading-spinner">Loading template editor...</div>
      </div>
    );
  }

  return (
    <div className="template-editor">
      <div className="editor-header">
        <div className="header-left">
          <h1>Submission Portal Template Editor</h1>
          <div className="template-selector">
            <label>Template:</label>
            <select
              value={selectedTemplateId}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
              className="template-select"
            >
              {templates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.name || template.id}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="header-actions">
          <button 
            onClick={() => createNewTemplate()}
            className="new-template-btn"
          >
            New Template
          </button>
          <button 
            onClick={() => duplicateTemplate()}
            className="duplicate-template-btn"
          >
            Duplicate Template
          </button>
          <button 
            onClick={() => deleteTemplate()}
            className="delete-template-btn"
            disabled={templates.length <= 1}
          >
            Delete Template
          </button>
          <button 
            onClick={handleSave} 
            disabled={saving}
            className="save-template-btn"
          >
            {saving ? 'Saving...' : `Save "${templates.find(t => t.id === selectedTemplateId)?.name || selectedTemplateId}"`}
          </button>
        </div>
      </div>

      <div className="editor-content">
        <div className="editor-sidebar">
          <nav className="section-nav">
            <button 
              className={`nav-item ${activeSection === 'template' ? 'active' : ''}`}
              onClick={() => setActiveSection('template')}
            >
              Template Settings
            </button>
            <button 
              className={`nav-item ${activeSection === 'general' ? 'active' : ''}`}
              onClick={() => setActiveSection('general')}
            >
              General Settings
            </button>
            <button 
              className={`nav-item ${activeSection === 'sermons' ? 'active' : ''}`}
              onClick={() => setActiveSection('sermons')}
            >
              Sermon Links
            </button>
            <button 
              className={`nav-item ${activeSection === 'questions' ? 'active' : ''}`}
              onClick={() => setActiveSection('questions')}
            >
              Disciplinary Questions
            </button>
            <button 
              className={`nav-item ${activeSection === 'statements' ? 'active' : ''}`}
              onClick={() => setActiveSection('statements')}
            >
              Statements
            </button>
            <button 
              className={`nav-item ${activeSection === 'custom' ? 'active' : ''}`}
              onClick={() => setActiveSection('custom')}
            >
              Custom Sections
            </button>
            <button 
              className={`nav-item ${activeSection === 'settings' ? 'active' : ''}`}
              onClick={() => setActiveSection('settings')}
            >
              Portal Settings
            </button>
          </nav>
        </div>

        <div className="editor-main">
          {activeSection === 'template' && (
            <div className="section-editor">
              <h2>Template Settings</h2>
              <div className="form-group">
                <label>Template Name</label>
                <input
                  type="text"
                  value={template.name || ''}
                  onChange={(e) => setTemplate({ ...template, name: e.target.value })}
                  placeholder="Enter template name (e.g., Local Pastor, Ministry Candidate)"
                />
                <small className="form-help">This name will appear in the template selector and throughout the system.</small>
              </div>
              <div className="form-group">
                <label>Conference Status</label>
                <input
                  type="text"
                  value={template.conferenceStatus || ''}
                  onChange={(e) => setTemplate({ ...template, conferenceStatus: e.target.value })}
                  placeholder="Enter conference status (e.g., Local Pastor, CLM, Ministry Candidate)"
                />
                <small className="form-help">This determines which candidates will use this template. Should match the conference status values in your system.</small>
              </div>
              <div className="form-group">
                <label>Template Description</label>
                <textarea
                  value={template.templateDescription || ''}
                  onChange={(e) => setTemplate({ ...template, templateDescription: e.target.value })}
                  rows="3"
                  placeholder="Describe what this template is for and who should use it..."
                />
                <small className="form-help">Internal description to help administrators understand the purpose of this template.</small>
              </div>
              <div className="template-info">
                <h3>Template Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Template ID:</label>
                    <span>{selectedTemplateId}</span>
                  </div>
                  <div className="info-item">
                    <label>Created:</label>
                    <span>
                      {template.createdAt ? 
                        new Date(template.createdAt.seconds * 1000).toLocaleDateString() : 
                        'Unknown'
                      }
                    </span>
                  </div>
                  <div className="info-item">
                    <label>Last Updated:</label>
                    <span>
                      {template.updatedAt ? 
                        new Date(template.updatedAt.seconds * 1000).toLocaleDateString() : 
                        'Never'
                      }
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'general' && (
            <div className="section-editor">
              <h2>General Settings</h2>
              <div className="form-group">
                <label>Portal Title</label>
                <input
                  type="text"
                  value={template.title}
                  onChange={(e) => setTemplate({ ...template, title: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Portal Description</label>
                <textarea
                  value={template.description}
                  onChange={(e) => setTemplate({ ...template, description: e.target.value })}
                  rows="3"
                />
              </div>
            </div>
          )}

          {activeSection === 'sermons' && (
            <div className="section-editor">
              <h2>Sermon Links Section</h2>
              <div className="section-toggle">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={template.sections.sermonLinks.enabled}
                    onChange={(e) => updateSection('sermonLinks', { enabled: e.target.checked })}
                  />
                  Enable Sermon Links Section
                </label>
              </div>
              
              {template.sections.sermonLinks.enabled && (
                <>
                  <div className="form-group">
                    <label>Section Title</label>
                    <input
                      type="text"
                      value={template.sections.sermonLinks.title}
                      onChange={(e) => updateSection('sermonLinks', { title: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Section Description</label>
                    <textarea
                      value={template.sections.sermonLinks.description}
                      onChange={(e) => updateSection('sermonLinks', { description: e.target.value })}
                      rows="3"
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Minimum Sermons Required</label>
                      <input
                        type="number"
                        min="0"
                        value={template.sections.sermonLinks.minSermons}
                        onChange={(e) => updateSection('sermonLinks', { minSermons: parseInt(e.target.value) })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Maximum Sermons Allowed</label>
                      <input
                        type="number"
                        min="1"
                        value={template.sections.sermonLinks.maxSermons}
                        onChange={(e) => updateSection('sermonLinks', { maxSermons: parseInt(e.target.value) })}
                      />
                    </div>
                  </div>
                  <div className="section-toggle">
                    <label className="toggle-label">
                      <input
                        type="checkbox"
                        checked={template.sections.sermonLinks.required}
                        onChange={(e) => updateSection('sermonLinks', { required: e.target.checked })}
                      />
                      Required Section
                    </label>
                  </div>
                </>
              )}
            </div>
          )}

          {activeSection === 'questions' && (
            <div className="section-editor">
              <h2>Disciplinary Questions</h2>
              <div className="section-toggle">
                <label className="toggle-label">
                  <input
                    type="checkbox"
                    checked={template.sections.disciplinaryQuestions.enabled}
                    onChange={(e) => updateSection('disciplinaryQuestions', { enabled: e.target.checked })}
                  />
                  Enable Disciplinary Questions Section
                </label>
              </div>
              
              {template.sections.disciplinaryQuestions.enabled && (
                <>
                  <div className="form-group">
                    <label>Section Title</label>
                    <input
                      type="text"
                      value={template.sections.disciplinaryQuestions.title}
                      onChange={(e) => updateSection('disciplinaryQuestions', { title: e.target.value })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Section Description</label>
                    <textarea
                      value={template.sections.disciplinaryQuestions.description}
                      onChange={(e) => updateSection('disciplinaryQuestions', { description: e.target.value })}
                      rows="3"
                    />
                  </div>
                  
                  <div className="questions-list">
                    <div className="questions-header">
                      <h3>Questions</h3>
                      <button onClick={addQuestion} className="add-question-btn">
                        Add Question
                      </button>
                    </div>
                    
                    {template.sections.disciplinaryQuestions.questions.map((question, index) => (
                      <div key={question.id} className="question-editor">
                        <div className="question-header">
                          <span className="question-number">{index + 1}.</span>
                          <div className="question-controls">
                            <label className="toggle-label">
                              <input
                                type="checkbox"
                                checked={question.enabled}
                                onChange={(e) => updateQuestion(question.id, { enabled: e.target.checked })}
                              />
                              Enabled
                            </label>
                            <label className="toggle-label">
                              <input
                                type="checkbox"
                                checked={question.required}
                                onChange={(e) => updateQuestion(question.id, { required: e.target.checked })}
                              />
                              Required
                            </label>
                            <button 
                              onClick={() => removeQuestion(question.id)}
                              className="remove-question-btn"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                        <textarea
                          value={question.question}
                          onChange={(e) => updateQuestion(question.id, { question: e.target.value })}
                          placeholder="Enter question text..."
                          rows="2"
                        />
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {activeSection === 'statements' && (
            <div className="section-editor">
              <h2>Statement Sections</h2>
              
              <div className="statement-section">
                <h3>Personal Statement</h3>
                <div className="section-toggle">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={template.sections.personalStatement.enabled}
                      onChange={(e) => updateSection('personalStatement', { enabled: e.target.checked })}
                    />
                    Enable Personal Statement
                  </label>
                </div>
                
                {template.sections.personalStatement.enabled && (
                  <>
                    <div className="form-group">
                      <label>Section Title</label>
                      <input
                        type="text"
                        value={template.sections.personalStatement.title}
                        onChange={(e) => updateSection('personalStatement', { title: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Section Description</label>
                      <textarea
                        value={template.sections.personalStatement.description}
                        onChange={(e) => updateSection('personalStatement', { description: e.target.value })}
                        rows="3"
                      />
                    </div>
                    <div className="form-group">
                      <label>Placeholder Text</label>
                      <textarea
                        value={template.sections.personalStatement.placeholder}
                        onChange={(e) => updateSection('personalStatement', { placeholder: e.target.value })}
                        rows="2"
                      />
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Minimum Length (characters)</label>
                        <input
                          type="number"
                          min="0"
                          value={template.sections.personalStatement.minLength}
                          onChange={(e) => updateSection('personalStatement', { minLength: parseInt(e.target.value) })}
                        />
                      </div>
                      <div className="form-group">
                        <label>Maximum Length (characters)</label>
                        <input
                          type="number"
                          min="1"
                          value={template.sections.personalStatement.maxLength}
                          onChange={(e) => updateSection('personalStatement', { maxLength: parseInt(e.target.value) })}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="statement-section">
                <h3>Call Statement</h3>
                <div className="section-toggle">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={template.sections.callStatement.enabled}
                      onChange={(e) => updateSection('callStatement', { enabled: e.target.checked })}
                    />
                    Enable Call Statement
                  </label>
                </div>
                
                {template.sections.callStatement.enabled && (
                  <>
                    <div className="form-group">
                      <label>Section Title</label>
                      <input
                        type="text"
                        value={template.sections.callStatement.title}
                        onChange={(e) => updateSection('callStatement', { title: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Section Description</label>
                      <textarea
                        value={template.sections.callStatement.description}
                        onChange={(e) => updateSection('callStatement', { description: e.target.value })}
                        rows="3"
                      />
                    </div>
                    <div className="form-group">
                      <label>Placeholder Text</label>
                      <textarea
                        value={template.sections.callStatement.placeholder}
                        onChange={(e) => updateSection('callStatement', { placeholder: e.target.value })}
                        rows="2"
                      />
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label>Minimum Length (characters)</label>
                        <input
                          type="number"
                          min="0"
                          value={template.sections.callStatement.minLength}
                          onChange={(e) => updateSection('callStatement', { minLength: parseInt(e.target.value) })}
                        />
                      </div>
                      <div className="form-group">
                        <label>Maximum Length (characters)</label>
                        <input
                          type="number"
                          min="1"
                          value={template.sections.callStatement.maxLength}
                          onChange={(e) => updateSection('callStatement', { maxLength: parseInt(e.target.value) })}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {activeSection === 'custom' && (
            <div className="section-editor">
              <h2>Custom Sections</h2>
              <div className="custom-sections-header">
                <p>Add custom sections to collect additional information from candidates.</p>
                <button onClick={addCustomSection} className="add-section-btn">
                  Add Custom Section
                </button>
              </div>
              
              {template.sections.additionalSections.map((section, index) => (
                <div key={section.id} className="custom-section-editor">
                  <div className="section-header">
                    <h4>Custom Section {index + 1}</h4>
                    <button 
                      onClick={() => removeCustomSection(section.id)}
                      className="remove-section-btn"
                    >
                      Remove
                    </button>
                  </div>
                  
                  <div className="form-group">
                    <label>Section Title</label>
                    <input
                      type="text"
                      value={section.title}
                      onChange={(e) => updateCustomSection(section.id, { title: e.target.value })}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Description</label>
                    <textarea
                      value={section.description}
                      onChange={(e) => updateCustomSection(section.id, { description: e.target.value })}
                      rows="2"
                    />
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label>Field Type</label>
                      <select
                        value={section.type}
                        onChange={(e) => updateCustomSection(section.id, { type: e.target.value })}
                      >
                        <option value="text">Text Input</option>
                        <option value="textarea">Text Area</option>
                        <option value="select">Dropdown</option>
                        <option value="radio">Radio Buttons</option>
                        <option value="checkbox">Checkboxes</option>
                        <option value="file">File Upload</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <div className="section-toggles">
                        <label className="toggle-label">
                          <input
                            type="checkbox"
                            checked={section.enabled}
                            onChange={(e) => updateCustomSection(section.id, { enabled: e.target.checked })}
                          />
                          Enabled
                        </label>
                        <label className="toggle-label">
                          <input
                            type="checkbox"
                            checked={section.required}
                            onChange={(e) => updateCustomSection(section.id, { required: e.target.checked })}
                          />
                          Required
                        </label>
                      </div>
                    </div>
                  </div>
                  
                  {(section.type === 'text' || section.type === 'textarea') && (
                    <div className="form-group">
                      <label>Placeholder Text</label>
                      <input
                        type="text"
                        value={section.placeholder || ''}
                        onChange={(e) => updateCustomSection(section.id, { placeholder: e.target.value })}
                      />
                    </div>
                  )}
                  
                  {(section.type === 'select' || section.type === 'radio' || section.type === 'checkbox') && (
                    <div className="form-group">
                      <label>Options (one per line)</label>
                      <textarea
                        value={(section.options || []).join('\n')}
                        onChange={(e) => updateCustomSection(section.id, { 
                          options: e.target.value.split('\n').filter(opt => opt.trim()) 
                        })}
                        rows="4"
                        placeholder="Option 1&#10;Option 2&#10;Option 3"
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {activeSection === 'settings' && (
            <div className="section-editor">
              <h2>Portal Settings</h2>
              <div className="settings-grid">
                <div className="setting-item">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={template.settings.allowDrafts}
                      onChange={(e) => setTemplate({
                        ...template,
                        settings: { ...template.settings, allowDrafts: e.target.checked }
                      })}
                    />
                    Allow candidates to save drafts
                  </label>
                </div>
                <div className="setting-item">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={template.settings.showProgress}
                      onChange={(e) => setTemplate({
                        ...template,
                        settings: { ...template.settings, showProgress: e.target.checked }
                      })}
                    />
                    Show progress indicator
                  </label>
                </div>
                <div className="setting-item">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={template.settings.emailNotifications}
                      onChange={(e) => setTemplate({
                        ...template,
                        settings: { ...template.settings, emailNotifications: e.target.checked }
                      })}
                    />
                    Send email notifications
                  </label>
                </div>
                <div className="setting-item">
                  <label className="toggle-label">
                    <input
                      type="checkbox"
                      checked={template.settings.deadlineReminders}
                      onChange={(e) => setTemplate({
                        ...template,
                        settings: { ...template.settings, deadlineReminders: e.target.checked }
                      })}
                    />
                    Send deadline reminders
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubmissionTemplateEditor;
